/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-underscore-dangle */
import _ from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import api from '~/services';
import * as lib from '~/dependencies';
import {
  log,
  getIntegrations,
  validateNumberType,
  dateParse,
  dateParseComplete,
  getDetailsFromTo,
} from '~/helpers';

import { filterOptions } from '~/store/reducers/historyFilterOptions';
import * as actionTypes from '~/store/actions/actionTypes';

dayjs.extend(utc);
dayjs.extend(timezone);

const convertSnakeLogsToCamelCase = calllogsResp => {
  return calllogsResp.map(call => ({
    id: call.call_id,
    /**
     * International dialing prefix format e.g: 003538300000000
     */
    internationalTo: call.to?.replace('+', '00'),
    /**
     * International dialing prefix format e.g: 003538300000000
     */
    internationalFrom: call.from?.replace('+', '00'),
    callInQueue: call.call_in_queue,
    callInQueueName: call.call_in_queue_name,
    fromSmsSupport: call.from_sms_support,
    toSmsSupport: call.to_sms_support,
    fromContactId: call.from_contact_id,
    toContactId: call.to_contact_id,
    toContactName: call.to_name_source === 'phonebook' ? call.to_name : null,
    fromContactName:
      call.from_name_source === 'phonebook' ? call.from_name : null,
    direction: call.direction,
    callId: call.call_id,
    from: call.from,
    fromLocal: call.from_local,
    fromName: call.from_name,
    fromExt: call.from_ext,
    fromExtName: call.from_ext_name,
    to: call.to,
    toName: call.to_name,
    toLocal: call.to_local,
    toExt: call.to_ext,
    toExtName: call.to_ext_name,
    status: call.status,
    duration: call.duration,
    callDate: call.call_date,
    callDirection: call.direction,
    recording: call.recording,
    soundPlay: true,
    answeredBy: call.answered_by,
    extra: call.extra,
    integrations: getIntegrations(call.extra),
    discriptiveTime: call.discriptive_time,
    lastApp: call.last_app,
    sipCallId: call?.sip_call_id,
    originalSipCallId: call?.original_sip_call_id,
  }));
};

export const setLoading = action => ({
  type: actionTypes.CALL_LOGS_STORE,
  payload: {
    loading: action,
  },
});

export const setFetchErrorMessage = message => ({
  type: actionTypes.CALL_LOGS_FAILURE,
  payload: {
    message,
    loading: false,
  },
});

export const clearHistoryCustomFilters = () => ({
  type: actionTypes.CLEAR_HISTORY_CUSTOM_FILTERS,
});

export const setOpenEnhancedSearch = open => ({
  type: actionTypes.CALL_LOGS_OPEN_ENHANCED_SEARCH,
  payload: {
    open,
  },
});

export const performResetCallItems = reset => ({
  type: actionTypes.RESET_CALL_ITEMS,
  payload: reset,
});

// export const resetHistoryFilterOptions = () => ({
//   type: actionTypes.RESET_HISTORY_FILTER_OPTIONS,
// });

export const setNoMoreItems = noMoreItems => ({
  type: actionTypes.CALL_NO_MORE_ITEMS,
  payload: { noMoreItems },
});

export const setCallDetails = call => (dispatch, getState) => {
  const { callDetails } = getState().call;
  const { extensions } = getState().phone;
  const { isIntegrated } = getState().integrations;

  const index = callDetails.findIndex(e => e.id === call.id);

  if (index === -1) {
    const { hours, minutes, seconds, timeParsed } = dateParse(
      call.duration,
      call.callDate
    );

    // Define calllog line header title
    const completeDate = dateParseComplete(call.callDate);

    const detailsFormmated = isIntegrated ? getDetailsFromTo(call) : call;

    const toAnsweredBy = extensions?.find(
      ext => ext.number === call.answeredBy && typeof ext.name !== 'undefined'
    );

    const newCallDetails = {
      ...detailsFormmated,
      toAnsweredBy: toAnsweredBy ? toAnsweredBy.name : null,
      dateFormatted: `${completeDate} ${timeParsed[0]}:${timeParsed[1]}`,
      durationFormatted: `${hours + minutes + seconds}`,
      numberTypeFrom: validateNumberType(
        call.fromLocal || call.from,
        call.userCountry
      ),
      numberType: validateNumberType(call.toLocal || call.to, call.userCountry),
    };

    dispatch({
      type: actionTypes.CALL_LOGS_CALL_DETAILS,
      payload: { callDetails: [...callDetails, newCallDetails] },
    });
  }
};

export const removeCallDetails = call => (dispatch, getState) => {
  const { callDetails } = getState().call;
  const newDetailsStack = callDetails.filter(e => e.id !== call.id);

  dispatch({
    type: actionTypes.CALL_LOGS_CALL_DETAILS,
    payload: { callDetails: newDetailsStack },
  });
};

export const resetCallDetails = () => (dispatch, getState) => {
  dispatch({
    type: actionTypes.CALL_LOGS_CALL_DETAILS,
    payload: { callDetails: [] },
  });
};

export const subtractOneWeek = start => ({
  type: actionTypes.SET_HISTORY_FILTER_OPTIONS,
  option: filterOptions.dateRange,
  payload: {
    start: dayjs(start)
      .subtract(1, 'day')
      .startOf('day')
      .format('YYYY-MM-DD HH:mm:ss'),
    end: dayjs(start)
      .startOf('day')
      .format('YYYY-MM-DD HH:mm:ss'),
  },
});

export const setPageOffset = page => ({
  type: actionTypes.SET_CALL_LOGS_PAGE_OFFSET,
  payload: {
    pageOffset: page,
  },
});

/**
 * Compouse the call history url with params
 * @returns a calllog url like /new/v1/call-log?...
 */
function createCalllogsParamsURL(selectedOptions) {
  const {
    dateRange,
    extensions: selectedExtensions,
    directions,
    queues,
    tags,
    types: selectedTypes,
    itemsPerPage,
    userExtension,
    searchFor,
    pageOffset,
  } = selectedOptions;

  let extensions;

  if (selectedTypes?.includes('All Calls')) {
    if (selectedExtensions.length) {
      extensions = selectedExtensions;
    } else {
      extensions = [];
    }
  } else {
    extensions = [userExtension, ...selectedExtensions];
  }

  const params = [
    `per_page=${itemsPerPage}`,
    `page=${pageOffset}`,
    ...(extensions?.length ? [`extensions=${extensions.join(',')}`] : []),
    ...(searchFor ? [`search_for=${searchFor}`] : []),
    ...(directions?.length ? [`type=${directions.join(',')}`] : []),
    ...(dateRange?.start ? [`from_date=${dateRange.start}`] : []),
    ...(dateRange?.end ? [`to_date=${dateRange.end}`] : []),
    ...(queues?.length ? [`queues=${queues.join(',')}`] : []),
    ...(tags?.length ? [`tags=${tags.join(',')}`] : []),
  ];

  return `${params.join('&')}&sort_by=call_date&sort_dir=desc`;
}

/**
 * Search for calls logs on the API
 * If <extension> is passed, it should returns "My Calls" only.
 * @requestCount - Used to control the maximum recursive requests.
 * @returns returns a call logs object or {}
 */
export const fetchCalllogs = (extension = null, requestCount = 1) => async (
  dispatch,
  getState
) => {
  try {
    const {
      resetCallItems,
      dateRange,
      hasDateRange,
      searchFor,
      pageOffset,
    } = getState().historyFilterOptions;

    const { phoneCallLogsAll, phoneCallLogsMy } = getState().call;
    const { userTimeZone } = getState().auth;

    const { start, end } = dateRange;
    const startDate = dayjs(start)
      .tz(userTimeZone)
      .format('YYYY-MM-DD HH:mm:ss');
    const endDate = dayjs(end)
      .tz(userTimeZone)
      .format('YYYY-MM-DD HH:mm:ss');

    const itemsPerPage = 100;
    const today = dayjs(new Date())
      .tz(userTimeZone)
      .format('DD MMMM YYYY');

    const pageState = extension ? 'phoneCallLogsMy' : 'phoneCallLogsAll';
    const maxRecursiveRequest = 30;

    // Represent the logs inside my or all calls
    let calllogsState = extension ? phoneCallLogsMy : phoneCallLogsAll;
    calllogsState = resetCallItems ? [] : calllogsState;

    dispatch(setLoading(true));
    dispatch(setNoMoreItems(false));

    const calllogsParamsURL = createCalllogsParamsURL({
      ...getState().historyFilterOptions,
      dateRange: {
        start: startDate,
        end: endDate,
      },
      itemsPerPage,
      userExtension: extension ?? '',
      searchFor,
    });

    const response = await api.calls.get.logsWithFilterOptions(
      calllogsParamsURL
    );
    const { page_count, page_size, total_items } = response.data;

    const hasMorePages = total_items / (page_count * page_size) >= 1;

    dispatch(setPageOffset(hasMorePages ? pageOffset + 1 : 1));

    /* NOTE: This is a recursive operation:
     * When there is no logs for the given period then the logic will call fetchCalllogs again and again until find the _embedded !== []
     */
    if (
      !hasMorePages &&
      !response.data._embedded.length &&
      requestCount < maxRecursiveRequest &&
      !hasDateRange
    ) {
      dispatch(subtractOneWeek(startDate));
      dispatch(fetchCalllogs(extension, requestCount + 1));
      return;
    }

    if (requestCount >= maxRecursiveRequest) {
      dispatch(setNoMoreItems(true));
    }

    if (resetCallItems) {
      dispatch(performResetCallItems(false));
    }

    // Format logs group name based on date
    const formatGroupName = logItem =>
      dayjs(logItem.callDate.split(' ')[0].replace(/-/g, '/')).format(
        'DD MMMM YYYY'
      );

    // Order a given date logs by the time descending
    const orderByTimeDesc = dateLogs =>
      dateLogs
        .map(call => ({ ...call, parsedDate: dayjs(call.callDate) }))
        .sort((a, b) => b.parsedDate - a.parsedDate)
        .map(({ ...rest }) => rest);

    // The API calllogs response data converted to camelcase
    const logsResponse = convertSnakeLogsToCamelCase(response.data._embedded);

    // Current calllogs data stored in redux
    const logsReducer = calllogsState.flatMap(item => item.data);

    // Merge response data with calllogs stored in redux
    const mergedResponseReducerLogs =
      logsReducer.length > 0 ? [...logsReducer, ...logsResponse] : logsResponse;

    // Avoid duplicated call ids
    const calllogs = mergedResponseReducerLogs.filter(
      (item, index, self) =>
        self.findIndex(obj => obj.callId === item.callId) === index
    );

    /*
     * Group calllogs by date e.g: ddd DD MMMM (NOT TIME)
     * const groupedLogs = { 25 July 2024: Array(31), ...}
     * */
    let groupedCalllogs = _.groupBy(calllogs, formatGroupName);

    groupedCalllogs = Object.keys(groupedCalllogs).map(createdAt => ({
      title:
        createdAt.toUpperCase() === today.toUpperCase()
          ? `Today ${dayjs(createdAt).format('ddd DD MMMM')}`
          : `${dayjs(createdAt).format('ddd DD MMMM')}`,
      data: orderByTimeDesc(groupedCalllogs[createdAt]),
      createdAt,
    }));

    // Sort the calllogs by createdAt in descending order (most recent first)
    groupedCalllogs = lib._.orderBy(
      groupedCalllogs,
      [item => dayjs(item.createdAt)],
      ['desc']
    );

    dispatch({
      type: actionTypes.CALL_LOGS_SUCCESS,
      payload: {
        [pageState]: groupedCalllogs,
        loading: false,
      },
    });
  } catch (err) {
    dispatch(setFetchErrorMessage(err.message));
  }
};

export const updateCallRecordingStatus = callId => async (
  dispatch,
  getState
) => {
  try {
    const { phoneCallLogsMy, phoneCallLogsAll, callDetails } = getState().call;

    const myCalls = phoneCallLogsMy.map(callsByDate => ({
      ...callsByDate,
      data: callsByDate.data.map(lg =>
        lg.callId === callId ? { ...lg, recording: false } : lg
      ),
    }));

    const allCalls = phoneCallLogsAll.map(callsByDate => ({
      ...callsByDate,
      data: callsByDate.data.map(lg =>
        lg.callId === callId ? { ...lg, recording: false } : lg
      ),
    }));

    dispatch({
      type: actionTypes.CALL_LOGS_UPDATE_RECORDING_STATUS,
      payload: {
        phoneCallLogsMy: myCalls,
        phoneCallLogsAll: allCalls,
        callDetails: callDetails?.length
          ? [{ ...callDetails[0], recording: false }]
          : callDetails,
      },
    });
  } catch (error) {
    log.error('Failed to update call recording status.', error);
  }
};

export const resetCalls = () => dispatch => {
  dispatch({
    type: actionTypes.CALL_LOGS_RESET,
  });
};
